import { useEffect, useRef, useState } from "react";

/**
 * Custom select dropdown that supports icons per option.
 * Replaces native <select> so icon components (e.g. FlagIcon) render correctly
 * inside options on all platforms including Windows.
 *
 * Props:
 *   value        - currently selected value string
 *   onChange     - (value: string) => void
 *   options      - [{ value, label, icon? }]  icon is an optional React node
 *   placeholder  - label shown when no value selected (default "Select…")
 *   placeholderIcon - optional React node shown next to placeholder
 *   variant      - "pill" (compact, rounded-full) | "form" (full-width, rounded-2xl)
 */
const CustomSelect = ({
  value,
  onChange,
  options,
  placeholder = "Select…",
  placeholderIcon = null,
  variant = "pill",
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selected = options.find((o) => o.value === value);

  const isPill = variant === "pill";

  const triggerClass = isPill
    ? "flex items-center gap-1.5 rounded-full border border-white/10 bg-slate-900 px-3 py-1 text-xs font-semibold text-white/70 hover:text-white focus:outline-none w-full"
    : "flex items-center gap-2 w-full rounded-2xl border border-white/10 bg-slate-900 px-4 py-3 text-sm font-semibold text-white hover:border-white/20 focus:border-brand-glow/60 focus:outline-none";

  const listClass = isPill
    ? "absolute left-0 top-full z-50 mt-1 max-h-64 w-48 overflow-y-auto rounded-2xl border border-white/10 bg-slate-900 py-1 shadow-xl"
    : "absolute left-0 top-full z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-2xl border border-white/10 bg-slate-900 py-1 shadow-xl";

  const optionClass = (isActive) =>
    `flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-white/5 ${
      isPill ? "text-xs" : "text-sm"
    } font-semibold ${isActive ? "text-white" : "text-white/60"}`;

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} className={triggerClass}>
        {selected ? (
          <>
            {selected.icon && <span className="shrink-0">{selected.icon}</span>}
            <span className="flex-1 truncate text-left">{selected.label}</span>
          </>
        ) : (
          <>
            {placeholderIcon && <span className="shrink-0">{placeholderIcon}</span>}
            <span className="flex-1 truncate text-left text-white/40">{placeholder}</span>
          </>
        )}
        <svg className="ml-auto h-3 w-3 shrink-0 opacity-50" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </button>

      {open && (
        <ul className={listClass}>
          {placeholder && (
            <li>
              <button type="button" onClick={() => { onChange(""); setOpen(false); }} className={optionClass(!value)}>
                {placeholderIcon && <span className="shrink-0">{placeholderIcon}</span>}
                <span>{placeholder}</span>
              </button>
            </li>
          )}
          {options.map((opt) => (
            <li key={opt.value}>
              <button type="button" onClick={() => { onChange(opt.value); setOpen(false); }} className={optionClass(value === opt.value)}>
                {opt.icon && <span className="shrink-0">{opt.icon}</span>}
                <span>{opt.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CustomSelect;
